/* BASIIC theme — upsells.js : bestseller/manual upsells + offer-code reconciliation */
(function () {
  'use strict';
  const B = window.basiic;
  if (!B || !B.cart) return;
  const SRC = window.basiicUpsellSource || null;
  const OFFERS = window.basiicOffers || {};
  const norm = (s) => String(s || '').trim().toUpperCase();

  /* ---------- upsell source (one fetch per page load) ---------- */
  let sourcePromise = null;
  function sourceUrl() {
    if (!SRC || !SRC.url) return null;
    const u = new URL(SRC.url, window.location.origin);
    u.searchParams.set('section_id', 'upsell-source');
    if (SRC.kind === 'collection') u.searchParams.set('sort_by', 'best-selling');
    return u.pathname + u.search;
  }
  function loadSource() {
    const url = sourceUrl();
    if (!url) return Promise.resolve(null);
    if (!sourcePromise) {
      sourcePromise = fetch(url, { credentials: 'same-origin' })
        .then((r) => { if (!r.ok) throw new Error('Upsell source ' + r.status); return r.text(); })
        .then((html) => {
          const root = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-upsell-source]');
          if (!root) return null;
          const kind = root.getAttribute('data-kind');
          if (kind === 'none') return null;
          // Never present a list as bestsellers unless Shopify sorted it best-selling.
          if (kind === 'best-selling' && root.getAttribute('data-sort') !== 'best-selling') return null;
          return { kind, items: Array.from(root.querySelectorAll('[data-upsell-item]')) };
        })
        .catch(() => { sourcePromise = null; return null; });
    }
    return sourcePromise;
  }
  const idsFromCart = (cart) => ((cart && cart.items) || []).map((i) => String(i.product_id));
  const splitIds = (s) => String(s || '').split(',').map((x) => x.trim()).filter(Boolean);
  let slotSeq = 0;

  class BasiicUpsells extends HTMLElement {
    connectedCallback() {
      this.uid = 'u' + (++slotSeq); // unique per mounted slot → no duplicate IDs across placements/re-renders
      this.list = this.querySelector('[data-upsell-list]');
      this.headingEl = this.querySelector('[data-upsell-heading]');
      this.onCart = () => this.render();
      document.addEventListener('cart:updated', this.onCart);
      document.addEventListener('cart:state', this.onCart);
      document.addEventListener('cart:rendered', this.onCart);
      this.render();
    }
    disconnectedCallback() {
      document.removeEventListener('cart:updated', this.onCart);
      document.removeEventListener('cart:state', this.onCart);
      document.removeEventListener('cart:rendered', this.onCart);
    }
    exclusions() {
      // Fixed exclusion (PDP product) is permanent; cart exclusions are always replaced by the freshest cart state.
      const fixed = splitIds(this.dataset.excludeFixed);
      const cartIds = B.cart.state && Array.isArray(B.cart.state.items) ? idsFromCart(B.cart.state) : splitIds(this.dataset.cartIds);
      return new Set(fixed.concat(cartIds));
    }
    async render() {
      const token = (this.token = (this.token || 0) + 1);
      const src = await loadSource();
      if (token !== this.token || !this.isConnected) return;
      if (!src) { this.hidden = true; return; }
      const exclude = this.exclusions();
      const limit = Math.max(1, parseInt(this.dataset.limit || '3', 10));
      const picked = src.items.filter((el) => !exclude.has(el.getAttribute('data-product-id'))).slice(0, limit);
      const signature = picked.map((el) => el.getAttribute('data-product-id')).join(',');
      if (!picked.length) { this.hidden = true; if (this.list) this.list.innerHTML = ''; this.signature = ''; return; }
      if (signature === this.signature && !this.hidden) return; // unchanged: keep current selections/focus
      this.signature = signature;
      const suffix = '-' + this.uid;
      const frag = document.createDocumentFragment();
      picked.forEach((el) => {
        const c = document.importNode(el, true);
        c.querySelectorAll('[id]').forEach((n) => { n.id = n.id + suffix; });
        c.querySelectorAll('label[for]').forEach((n) => { n.htmlFor = n.htmlFor + suffix; });
        frag.appendChild(c);
      });
      if (this.list) { this.list.innerHTML = ''; this.list.appendChild(frag); }
      if (this.headingEl) {
        this.headingEl.textContent = src.kind === 'manual' ? (this.dataset.headingManual || '') : (this.dataset.headingBestseller || '');
        this.headingEl.hidden = !this.headingEl.textContent;
      }
      this.hidden = false;
    }
  }
  if (!customElements.get('basiic-upsells')) customElements.define('basiic-upsells', BasiicUpsells);

  /* ---------- offer codes (code mode only; automatic discounts need no theme action) ---------- */
  if (!OFFERS.codeMode) return;

  let lastMessage = '';
  function setMessage(msg) {
    lastMessage = msg || '';
    document.querySelectorAll('[data-offer-message]').forEach((el) => { el.textContent = lastMessage; el.hidden = !lastMessage; });
  }
  document.addEventListener('cart:rendered', () => setMessage(lastMessage));

  const rejected = new Map(); // CODE -> cart signature at rejection; retried only once the bag changes
  const signature = (cart, state) => cart.items.map((i) => i.key + ':' + i.quantity).join('|') + '#' + state.eligible;

  async function fetchJSON(url) {
    const r = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!r.ok) throw new Error(String(r.status));
    return r.json();
  }
  async function fetchState() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const r = await fetch(root + '?section_id=offer-state', { credentials: 'same-origin' });
    if (!r.ok) throw new Error(String(r.status));
    const el = new DOMParser().parseFromString(await r.text(), 'text/html').querySelector('[data-offer-state]');
    if (!el) throw new Error('offer-state missing');
    return JSON.parse(el.textContent);
  }
  const confirmed = (codes, code) => { const d = (codes || []).find((x) => norm(x.code) === code); return !!(d && d.applicable === true); };

  // Runs INSIDE the cart queue: reads fresh cart + server-derived eligibility, then uses raw B.cart._update (no nested enqueue).
  async function reconcileTask() {
    queued = false;
    let state; let cart;
    try { state = await fetchState(); cart = await fetchJSON(window.routes.cart_url + '.js'); }
    catch (e) { return; } // nothing changed, nothing claimed
    const managed = new Set((state.managed || []).map(norm).filter(Boolean));
    if (!managed.size || !Array.isArray(cart.discount_codes)) return;
    const sig = signature(cart, state);
    const current = cart.discount_codes.map((d) => d.code);
    const others = current.filter((c) => !managed.has(norm(c))); // unrelated codes preserved from FRESH state
    const want = [];
    if (state.target) want.push(norm(state.target));
    if (state.wantItem && state.itemCode) want.push(norm(state.itemCode));
    const wantUsable = Array.from(new Set(want)).filter((c) => rejected.get(c) !== sig);
    const next = others.concat(wantUsable);
    const already = next.length === current.length && next.every((c) => current.some((x) => norm(x) === norm(c)));
    try {
      let res = cart;
      if (!already) res = await B.cart._update({ discount: next.join(',') });
      // HTTP 200 is not confirmation: only applicable === true counts.
      const bad = wantUsable.filter((c) => !confirmed(res && res.discount_codes, c));
      if (bad.length) {
        bad.forEach((c) => rejected.set(c, sig));
        await B.cart._update({ discount: next.filter((c) => !bad.includes(norm(c))).join(',') });
        setMessage(OFFERS.errorMessage || '');
      } else {
        setMessage('');
      }
    } catch (e) {
      setMessage(OFFERS.errorMessage || '');
    }
  }
  // Coalesce: one queued reconciliation is enough because it reads fresh state when it starts.
  let queued = false; let queuedRun = null;
  function reconcile() {
    if (queued) return queuedRun;
    queued = true;
    queuedRun = B.cart.enqueue(reconcileTask);
    return queuedRun;
  }
  B.offers = { reconcile };
  B.cart.hooks.push(reconcile);

  // Checkout waits for any queued add/change and the code evaluation that follows it.
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!form || !/^CartForm-/.test(form.id || '')) return;
    const submitter = e.submitter;
    if (!submitter || submitter.name !== 'checkout') return;
    if (form.dataset.checkoutReady === 'true') return;
    e.preventDefault();
    submitter.setAttribute('aria-disabled', 'true');
    try { await reconcile(); await B.cart.settle(); } catch (err) { /* proceed; Shopify re-evaluates at checkout */ }
    // The cart form may have been re-rendered while waiting: submit the live one natively.
    const live = document.getElementById(form.id);
    const btn = live && document.querySelector(`[name="checkout"][form="${form.id}"]`);
    if (live && btn && live.requestSubmit) { live.dataset.checkoutReady = 'true'; live.requestSubmit(btn); }
    else { const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; window.location.href = root + 'checkout'; }
  }, true);

  reconcile();
})();
